import React, { useState } from 'react';
import { FaPlus, FaSearch, FaClock } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CardComponent from '../../components/cards/CardComponent';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import MaterialButton from '../../components/Buttons/MaterialButton';

const WorkSchedule = () => {
  const [schedules, setSchedules] = useState([
    {
      id: 1,
      schedule_name: 'Kitchen Day Shift',
      start_time: '08:00',
      end_time: '16:00',
      break_duration: 60,
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      active: true,
      description: 'Day shift schedule for kitchen staff'
    },
    {
      id: 2,
      schedule_name: 'Service Evening Shift',
      start_time: '16:00',
      end_time: '00:00',
      break_duration: 45,
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      active: true,
      description: 'Evening shift schedule for service staff'
    },
    {
      id: 3,
      schedule_name: 'Weekend Morning Shift',
      start_time: '07:00',
      end_time: '15:00',
      break_duration: 30,
      working_days: ['Saturday', 'Sunday'],
      active: false,
      description: 'Weekend morning shift for special events'
    }
  ]);
  
  const [loading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [formData, setFormData] = useState({
    schedule_name: '',
    start_time: '',
    end_time: '',
    break_duration: '',
    working_days: [],
    description: '',
    status: 'active'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.schedule_name.trim()) {
      toast.error('Schedule name is required');
      return;
    }
    
    if (!formData.start_time) {
      toast.error('Start time is required');
      return;
    }
    
    if (!formData.end_time) {
      toast.error('End time is required');
      return;
    }
    
    if (!formData.break_duration) {
      toast.error('Break duration is required');
      return;
    }
    
    if (formData.working_days.length === 0) {
      toast.error('Please select at least one working day');
      return;
    }
    
    try {
      if (editingSchedule) {
        // Update schedule
        const updatedSchedules = schedules.map(schedule => 
          schedule.id === editingSchedule.id 
            ? {
                ...schedule,
                schedule_name: formData.schedule_name,
                start_time: formData.start_time,
                end_time: formData.end_time,
                break_duration: parseInt(formData.break_duration),
                working_days: formData.working_days,
                description: formData.description,
                active: formData.status === 'active'
              }
            : schedule
        );
        setSchedules(updatedSchedules);
        toast.success('Schedule updated successfully!');
      } else {
        // Create new schedule
        const newSchedule = {
          id: Math.max(...schedules.map(s => s.id)) + 1,
          schedule_name: formData.schedule_name,
          start_time: formData.start_time,
          end_time: formData.end_time,
          break_duration: parseInt(formData.break_duration),
          working_days: formData.working_days,
          description: formData.description,
          active: formData.status === 'active'
        };
        setSchedules([...schedules, newSchedule]);
        toast.success('Schedule created successfully!');
      }
      setShowModal(false);
      setEditingSchedule(null);
      setFormData({ 
        schedule_name: '', 
        start_time: '', 
        end_time: '', 
        break_duration: '', 
        working_days: [], 
        description: '', 
        status: 'active' 
      });
    } catch (error) {
      toast.error('Failed to save schedule');
      console.error('Error saving schedule:', error);
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      schedule_name: schedule.schedule_name,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      break_duration: schedule.break_duration.toString(),
      working_days: schedule.working_days,
      description: schedule.description || '',
      status: schedule.active ? 'active' : 'inactive'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        const updatedSchedules = schedules.filter(schedule => schedule.id !== id);
        setSchedules(updatedSchedules);
        toast.success('Schedule deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete schedule');
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const handleDayToggle = (day) => {
    const updatedDays = formData.working_days.includes(day)
      ? formData.working_days.filter(d => d !== day)
      : [...formData.working_days, day];
    setFormData({ ...formData, working_days: updatedDays });
  };

  const filteredSchedules = schedules.filter(schedule =>
    schedule.schedule_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (schedule.description && schedule.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Layout>
      <Header title="Work Schedule Management" />
      
      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <CardComponent
            title="Work Schedule Management System"
            headerColor="primary"
            pull="left"
            bodyClass="panel-body"
            headerContent={
              <div className="d-flex align-items-center">
                <i className="zmdi zmdi-time me-2"></i>
                <span>Schedule Management & Time Tracking</span>
              </div>
            }
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5>Work Schedules</h5>
                <p className="text-muted">Manage employee work schedules and shifts</p>
              </div>
              <div className="d-flex gap-2">
                <div className="d-flex gap-2 me-2">
                  <MaterialButton
                    variant={viewMode === 'card' ? 'warning' : 'outline-warning'}
                    size="sm"
                    onClick={() => setViewMode('card')}
                    className="d-flex align-items-center"
                  >
                    <i className="zmdi zmdi-view-module me-1"></i>
                    Cards
                  </MaterialButton>
                  <MaterialButton
                    variant={viewMode === 'table' ? 'info' : 'outline-info'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    className="d-flex align-items-center"
                  >
                    <i className="zmdi zmdi-view-list me-1"></i>
                    Table
                  </MaterialButton>
                </div>
                <MaterialButton 
                  variant="primary" 
                  onClick={() => {
                    setEditingSchedule(null);
                    setFormData({ 
                      schedule_name: '', 
                      start_time: '', 
                      end_time: '', 
                      break_duration: '', 
                      working_days: [], 
                      description: '', 
                      status: 'active' 
                    });
                    setShowModal(true);
                  }}
                  icon="zmdi zmdi-plus"
                  rounded={true}
                >
                  Add New Schedule
                </MaterialButton>
              </div>
            </div>

            
            {/* Statistics Cards */}
            <div className="row">
              <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12 mb-3">
                <div className="card h-100 shadow-lg border-0" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '15px',
                  transform: 'translateY(0)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <div className="card-body text-center text-white p-3">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      <div className="rounded-circle p-2" style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <i className="zmdi zmdi-time" style={{ fontSize: '1.8rem' }}></i>
                      </div>
                    </div>
                    <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                      {loading ? '...' : schedules.length}
                    </h2>
                    <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>Total Schedules</p>
                    <div className="mt-2">
                      <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                        <div className="progress-bar bg-white" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12 mb-3">
                <div className="card h-100 shadow-lg border-0" style={{
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  borderRadius: '15px',
                  transform: 'translateY(0)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <div className="card-body text-center text-white p-3">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      <div className="rounded-circle p-2" style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <i className="zmdi zmdi-check-circle" style={{ fontSize: '1.8rem' }}></i>
                      </div>
                    </div>
                    <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                      {loading ? '...' : schedules.filter(s => s.active).length}
                    </h2>
                    <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>Active Schedules</p>
                    <div className="mt-2">
                      <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                        <div className="progress-bar bg-white" 
                             style={{ width: `${schedules.length > 0 ? (schedules.filter(s => s.active).length/schedules.length)*100 : 0}%` }}>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12 mb-3">
                <div className="card h-100 shadow-lg border-0" style={{
                  background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                  borderRadius: '15px',
                  transform: 'translateY(0)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <div className="card-body text-center text-white p-3">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      <div className="rounded-circle p-2" style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <i className="zmdi zmdi-calendar" style={{ fontSize: '1.8rem' }}></i>
                      </div>
                    </div>
                    <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                      {loading ? '...' : Math.round(schedules.reduce((sum, s) => sum + s.working_days.length, 0) / schedules.length || 0)}
                    </h2>
                    <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>Avg. Work Days</p>
                    <div className="mt-2">
                      <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                        <div className="progress-bar bg-white" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Search and Filter Section */}
            <div className="p-4 border-bottom rounded" style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderBottom: '1px solid #dee2e6'
            }}>
              <div className="row g-3 align-items-center">
                <div className="col-lg-6 col-md-6">
                  <div className="position-relative">
                    <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-primary" />
                    <input
                      type="text"
                      className="form-control ps-5 rounded-pill border-2 shadow-sm"
                      style={{
                        borderColor: '#e3f2fd',
                        backgroundColor: 'white',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="🔍 Search schedules..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Layout - Card or Table View */}
            {viewMode === 'card' ? (
              <div className="row g-4">
              {loading ? (
                <div className="col-12">
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted fs-5">Loading schedules...</p>
                  </div>
                </div>
              ) : filteredSchedules.length === 0 ? (
                <div className="col-12">
                  <div className="text-center py-5">
                    <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{width: '120px', height: '120px'}}>
                      <FaClock className="text-muted" size={50} />
                    </div>
                    <h4 className="text-muted mb-3">No schedules found</h4>
                    <p className="text-muted fs-5">Start by creating your first schedule</p>
                    <MaterialButton 
                      variant="primary" 
                      onClick={() => {
                        setEditingSchedule(null);
                        setFormData({ 
                          schedule_name: '', 
                          start_time: '', 
                          end_time: '', 
                          break_duration: '', 
                          working_days: [], 
                          description: '', 
                          status: 'active' 
                        });
                        setShowModal(true);
                      }}
                      icon="zmdi zmdi-plus"
                      rounded={true}
                      className="mt-3"
                    >
                      Create First Schedule
                    </MaterialButton>
                  </div>
                </div>
              ) : (
                filteredSchedules.map((schedule, index) => (
                  <div key={schedule.id} className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
                    <div className="card h-100 shadow-sm border-0 position-relative overflow-hidden" style={{
                      borderRadius: '16px',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                    }}>
                      {/* Status Indicator */}
                      <div className={`position-absolute top-0 end-0 badge ${schedule.active ? 'bg-success' : 'bg-secondary'}`} 
                           style={{ borderRadius: '0 16px 0 16px', fontSize: '0.75rem' }}>
                        {schedule.active ? 'Active' : 'Inactive'}
                      </div>
                      
                      {/* Card Header */}
                      <div className="card-header border-0 bg-transparent pt-4">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle p-3 me-3" style={{
                              background: schedule.active 
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                                : 'linear-gradient(135deg, #a8a8a8 0%, #6c757d 100%)',
                              color: 'white'
                            }}>
                              <FaClock size={20} />
                            </div>
                          </div>
                          <div>
                            <span className="badge bg-light text-dark rounded-pill px-3 py-1" style={{ fontSize: '0.7rem' }}>
                              ID: {schedule.id}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Card Body */}
                      <div className="card-body pt-2">
                        <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                          {schedule.schedule_name}
                        </h5>
                        
                        {/* Time Info */}
                        <div className="mb-3">
                          <span className="badge bg-info text-white rounded-pill px-3 py-2 me-2" style={{ fontSize: '0.8rem' }}>
                            {schedule.start_time} - {schedule.end_time}
                          </span>
                          <span className="badge bg-warning text-dark rounded-pill px-3 py-2" style={{ fontSize: '0.8rem' }}>
                            Break: {schedule.break_duration}min
                          </span>
                        </div>

                        {/* Working Days */}
                        <div className="mb-3">
                          <small className="text-muted">Working Days:</small>
                          <div className="mt-1">
                            {schedule.working_days.map(day => (
                              <span key={day} className="badge bg-primary me-1 mb-1" style={{ fontSize: '0.7rem' }}>
                                {day.substring(0, 3)}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <p className="card-text text-muted mb-4" style={{ 
                          fontSize: '0.9rem', 
                          lineHeight: '1.5',
                          minHeight: '3rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {schedule.description || 'No description available for this schedule.'}
                        </p>
                        
                        {/* Action Buttons */}
                        <div className="d-flex gap-2">
                          <MaterialButton 
                            variant="primary" 
                            onClick={() => handleEdit(schedule)}
                            icon="zmdi zmdi-edit"
                            rounded={true}
                            title="Edit Schedule"
                            size="sm"
                          >
                            Edit
                          </MaterialButton>
                          <MaterialButton 
                            variant="danger" 
                            onClick={() => handleDelete(schedule.id)}
                            icon="zmdi zmdi-delete"
                            rounded={true}
                            title="Delete Schedule"
                            size="sm"
                          >
                            Delete
                          </MaterialButton>
                        </div>
                      </div>
                      
                      {/* Hover Effect */}
                      <style jsx>{`
                        .card:hover {
                          transform: translateY(-5px);
                          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
                        }
                      `}</style>
                    </div>
                  </div>
                ))
              )}
              </div>
            ) : (
              /* Table View */
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted fs-5">Loading schedules...</p>
                  </div>
                ) : filteredSchedules.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{width: '120px', height: '120px'}}>
                      <FaClock className="text-muted" size={50} />
                    </div>
                    <h4 className="text-muted mb-3">No schedules found</h4>
                    <p className="text-muted fs-5">Start by creating your first schedule</p>
                  </div>
                ) : (
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th width="8%" className="text-center">ID</th>
                        <th width="20%">Schedule Name</th>
                        <th width="15%">Time</th>
                        <th width="10%">Break</th>
                        <th width="25%">Working Days</th>
                        <th width="10%" className="text-center">Status</th>
                        <th width="12%" className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSchedules.map((schedule, index) => (
                        <tr key={schedule.id}>
                          <td className="text-center">
                            {schedule.id}
                          </td>
                          <td>
                            {schedule.schedule_name}
                          </td>
                          <td>
                            <span className="badge bg-info text-white">
                              {schedule.start_time} - {schedule.end_time}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-warning text-dark">
                              {schedule.break_duration}min
                            </span>
                          </td>
                          <td>
                            {schedule.working_days.map(day => (
                              <span key={day} className="badge bg-primary me-1 mb-1" style={{ fontSize: '0.7rem' }}>
                                {day.substring(0, 3)}
                              </span>
                            ))}
                          </td>
                          <td className="text-center">
                            <span className={`badge ${schedule.active ? 'bg-success' : 'bg-secondary'}`}>
                              {schedule.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-center">
                            <MaterialButton 
                              variant="primary" 
                              onClick={() => handleEdit(schedule)}
                              icon="zmdi zmdi-edit"
                              rounded={true}
                              title="Edit Schedule"
                              size="sm"
                              className="me-1"
                            />
                            <MaterialButton 
                              variant="danger" 
                              onClick={() => handleDelete(schedule.id)}
                              icon="zmdi zmdi-delete"
                              rounded={true}
                              title="Delete Schedule"
                              size="sm"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </CardComponent>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <FaClock className="me-2" />
                  {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Schedule Name *</label>
                      <input
                        type="text"
                        className="form-control rounded-pill"
                        value={formData.schedule_name}
                        onChange={(e) => setFormData({...formData, schedule_name: e.target.value})}
                        placeholder="Enter schedule name"
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Start Time *</label>
                      <input
                        type="time"
                        className="form-control rounded-pill"
                        value={formData.start_time}
                        onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">End Time *</label>
                      <input
                        type="time"
                        className="form-control rounded-pill"
                        value={formData.end_time}
                        onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Break Duration (minutes) *</label>
                      <input
                        type="number"
                        className="form-control rounded-pill"
                        value={formData.break_duration}
                        onChange={(e) => setFormData({...formData, break_duration: e.target.value})}
                        placeholder="Enter break duration in minutes"
                        min="0"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        className="form-select rounded-pill"
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Working Days *</label>
                      <div className="d-flex flex-wrap gap-2">
                        {daysOfWeek.map(day => (
                          <div key={day} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={day}
                              checked={formData.working_days.includes(day)}
                              onChange={() => handleDayToggle(day)}
                            />
                            <label className="form-check-label" htmlFor={day}>
                              {day}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Enter schedule description"
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary rounded-pill"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4"
                  >
                    <FaPlus className="me-2" />
                    {editingSchedule ? 'Update' : 'Create'} Schedule
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
};

export default WorkSchedule;
